import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './ApplicationAnnouncement.css';

// Default announcement for new users or when API fails
const DEFAULT_ANNOUNCEMENT = {
    announcemnt_data: "Introducing a new feature! You can now upload reports to your Acme Health account by sending an email to support@acmehealth.ai with your reports attached from your Acme Health-registered email address."
};

const ApplicationAnnouncement = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const userData = useSelector((state) => state.apiService.login);
    const token = useSelector((state) => state.apiService.token);

    useEffect(() => {
        // Check if we have announcements from the login response
        const initializeAnnouncements = () => {
            setIsLoading(true);
            
            // Check if announcements_get is not null
            if (userData?.announcements_get !== null) {
                if (userData?.announcements_get && Array.isArray(userData.announcements_get) && userData.announcements_get.length > 0) {
                    console.log("Using announcements from login response:", userData.announcements_get);
                    setAnnouncements(userData.announcements_get);
                } else {
                    // announcements_get is not null but empty/invalid, use default
                    console.log("announcements_get is not null but empty, using default announcement");
                    setAnnouncements([DEFAULT_ANNOUNCEMENT]);
                }
            } else {
                // announcements_get is null, don't use default announcement
                console.log("announcements_get is null, not using default announcement");
                setAnnouncements([]);
            }
            
            setIsLoading(false);
        };

        initializeAnnouncements();
    }, [userData]);

    useEffect(() => {
        if (announcements && announcements.length > 1) {
            // Auto-advance carousel every 8 seconds if multiple announcements
            const interval = setInterval(() => {
                setCurrentIndex((prevIndex) => 
                    prevIndex === announcements.length - 1 ? 0 : prevIndex + 1
                );
            }, 8000);

            return () => clearInterval(interval);
        }
    }, [announcements]);

    // Auto-hide announcement after 15 seconds (increased from 10)
    useEffect(() => {
        if (announcements && announcements.length > 0 && isVisible) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                localStorage.setItem('announcementDismissed', 'true');
            }, 15000); // 15 seconds

            return () => clearTimeout(timer);
        }
    }, [announcements, isVisible]);

    // Check if user previously dismissed announcement, but ignore for new users (first time login)
    useEffect(() => {
        const dismissed = localStorage.getItem('announcementDismissed');
        const isFirstTimeLogin = localStorage.getItem('isFirstTimeLogin');
        const hasSeenAnnouncement = localStorage.getItem('hasSeenAnnouncement');
        
        // Show announcement if it's first time login OR if user has never seen an announcement
        if (isFirstTimeLogin === 'true' || !hasSeenAnnouncement) {
            setIsVisible(true);
            localStorage.setItem('hasSeenAnnouncement', 'true');
        } else if (dismissed === 'true') {
            setIsVisible(false);
        }
        
        // Clear the first time login flag after checking
        if (isFirstTimeLogin === 'true') {
            localStorage.removeItem('isFirstTimeLogin');
        }
    }, []);

    // Don't render if loading or user dismissed it (but show for new users)
    if (isLoading || !isVisible) {
        return null;
    }

    // Always show announcement if we have data (either from API or default)
    if (!announcements || announcements.length === 0) {
        return null;
    }

    const currentAnnouncement = announcements[currentIndex];

    // const handleDismiss = () => {
    //     setIsVisible(false);
    //     localStorage.setItem('announcementDismissed', 'true');
    // };

    return (
        <div className="pmr-announcement-banner">
            <Confetti />
            <div className="pmr-announcement-content">
                <div className="pmr-announcement-text">
                    <span className="pmr-announcement-message">
                        🎉 {currentAnnouncement.announcemnt_data}
                    </span>
                </div>
                
                {/* Close button */}
                {/* <button className="pmr-announcement-close" onClick={handleDismiss}>
                    ×
                </button> */}
                
                {/* Dots indicator for multiple announcements */}
                {announcements.length > 1 && (
                    <div className="pmr-announcement-dots">
                        {announcements.map((_, index) => (
                            <span
                                key={index}
                                className={`pmr-dot ${index === currentIndex ? 'active' : ''}`}
                                onClick={() => setCurrentIndex(index)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const Confetti = () => (
    <div className="pmr-confetti">
        {[...Array(40)].map((_, i) => (
            <div
                key={i}
                style={{
                    position: 'absolute',
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    width: 4 + Math.random() * 8,
                    height: 4 + Math.random() * 8,
                    background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE'][i % 10],
                    borderRadius: Math.random() > 0.5 ? '50%' : '0%',
                    opacity: 0.7 + Math.random() * 0.3,
                    animation: `pmr-fall ${2 + Math.random() * 3}s linear infinite`,
                    animationDelay: `${Math.random() * 3}s`
                }}
            />
        ))}
        <style>
            {`
                @keyframes pmr-fall {
                    0% { 
                        transform: translateY(-20px) rotate(0deg); 
                        opacity: 1; 
                    }
                    100% { 
                        transform: translateY(120px) rotate(360deg); 
                        opacity: 0; 
                    }
                }
            `}
        </style>
    </div>
);

export default ApplicationAnnouncement; 